<template>
  <div class="portal-services" :dir="lang === 'fa' ? 'rtl' : 'ltr'">
    <div class="section-intro">
      <p>{{ copy.body }}</p>
    </div>

    <div class="services-grid">
      <section
        v-for="group in primaryGroups"
        :key="group.id"
        class="card service-group"
        :aria-labelledby="'service-' + group.id"
      >
        <div class="card-head">
          <div class="card-title">
            <span class="card-ico"><svg class="icon" aria-hidden="true"><use :href="'#' + group.icon"/></svg></span>
            <h2 :id="'service-' + group.id">{{ copy[group.id] }}</h2>
          </div>
        </div>
        <ul class="service-links">
          <li v-for="item in group.items" :key="item.route">
            <router-link class="service-link" :to="r(item.route)">
              <span>{{ copy[item.label] }}</span>
              <svg class="icon icon-sm" aria-hidden="true"><use href="#i-arrow"/></svg>
            </router-link>
          </li>
        </ul>
        <p v-if="group.id === 'assessments'" class="service-note">{{ copy.assessmentsNote }}</p>
      </section>
    </div>

    <section class="card service-group service-secondary spaced" aria-labelledby="service-extras">
      <div class="card-head">
        <div>
          <h2 id="service-extras">{{ copy.extras }}</h2>
          <p>{{ copy.extrasNote }}</p>
        </div>
      </div>
      <ul class="service-links">
        <li v-for="item in secondaryGroup.items" :key="item.route">
          <router-link class="service-link" :to="r(item.route)">
            <span>{{ copy[item.label] }}</span>
            <svg class="icon icon-sm" aria-hidden="true"><use href="#i-arrow"/></svg>
          </router-link>
          <p v-if="item.route === 'chatGpt'" class="service-note">{{ copy.aiNote }}</p>
        </li>
      </ul>
    </section>
    <div class="actions spaced">
      <button class="btn btn-secondary" type="button" @click="logout">{{ copy.logout }}</button>
    </div>
  </div>
</template>

<script>
const GROUPS = [
  {id: 'account', icon: 'i-user', items: [{route: 'setting', label: 'settings'}]},
  {id: 'care', icon: 'i-path', items: [
    {route: 'portalSessions', label: 'sessions'},
    {route: 'portalProgress', label: 'progress'},
    {route: 'portalChat', label: 'chat'},
    {route: 'tasks', label: 'tasks'},
  ]},
  {id: 'payments', icon: 'i-card', items: [
    {route: 'portalPayments', label: 'paymentHistory'},
    {route: 'portalPackage', label: 'packages'},
    {route: 'basket', label: 'basket'},
  ]},
  {id: 'assessments', icon: 'i-chart', items: [{route: 'tests', label: 'tests'}]},
  {id: 'support', icon: 'i-support', items: [
    {route: 'portalSupport', label: 'requests'},
    {route: 'intakeNotify', label: 'notifications'},
  ]},
  {id: 'extras', secondary: true, items: [
    {route: 'invitees', label: 'invitees'},
    {route: 'roots-in-soil', label: 'roots'},
    {route: 'chatGpt', label: 'ai'},
  ]},
]
const COPY = {
  fa: {
    title: 'خدمات و حساب من',
    body: 'دسترسی به حساب، مسیر درمان، پرداخت‌ها و خدمات موجود.',
    account: 'حساب کاربری', settings: 'اطلاعات و تنظیمات حساب',
    logout: 'خروج از حساب',
    care: 'مسیر درمان', sessions: 'جلسه‌های من', progress: 'روند درمان من',
    chat: 'پیام با درمانگر', tasks: 'تکلیف‌های من',
    payments: 'پرداخت‌ها', paymentHistory: 'اعتبار، پرداخت‌ها و رسیدها',
    packages: 'خرید بسته جلسه', basket: 'سبد خرید',
    assessments: 'خودارزیابی', tests: 'آزمون‌ها و سوابق من',
    assessmentsNote: 'فقط آزمون‌های پشتیبانی‌شده در این بخش در دسترس هستند.',
    support: 'پشتیبانی و اعلان‌ها', requests: 'درخواست‌های پشتیبانی', notifications: 'اعلان‌های من',
    extras: 'سایر خدمات موجود',
    extrasNote: 'دسترسی‌های جانبی حساب؛ این فهرست پیشنهاد درمانی نیست.',
    invitees: 'دعوت دوستان', roots: 'ریشه در خاک', ai: 'ابزار هوش مصنوعی',
    aiNote: 'این ابزار از سرویس هوش مصنوعی بیرونی استفاده می‌کند و در دسترس بودن آن به همان سرویس وابسته است. جایگزین گفت‌وگو با درمانگر نیست.',
  },
  en: {
    title: 'My services and account',
    body: 'Access your account, care journey, payments and existing services.',
    account: 'Account', settings: 'Account information and settings',
    logout: 'Sign out',
    care: 'Care journey', sessions: 'My sessions', progress: 'My treatment progress',
    chat: 'Message my therapist', tasks: 'My assignments',
    payments: 'Payments', paymentHistory: 'Credit, payments and receipts',
    packages: 'Buy a session package', basket: 'Shopping basket',
    assessments: 'Self-assessments', tests: 'My tests and history',
    assessmentsNote: 'Only supported tests are available in this section.',
    support: 'Support and notifications', requests: 'Support requests', notifications: 'My notifications',
    extras: 'Other existing services',
    extrasNote: 'Additional account services; this list is not a treatment recommendation.',
    invitees: 'Invite friends', roots: 'Roots in soil', ai: 'AI tool',
    aiNote: 'This tool uses an external AI service and depends on that service being available. It does not replace talking with your therapist.',
  },
}

export default {
  name: 'PortalServicesStep',
  computed: {
    lang() { return this.$route.params.lang === 'en' ? 'en' : 'fa' },
    copy() { return COPY[this.lang] },
    primaryGroups() { return GROUPS.filter(group => !group.secondary) },
    secondaryGroup() { return GROUPS.find(group => group.secondary) },
  },
  methods: {
    r(name) { return {name, params: {lang: this.lang}} },
  },
}
</script>

<style scoped>
.services-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
.service-group { min-width: 0; }
.service-group .card-head { margin-bottom: .5rem; }
.service-links { list-style: none; margin: 0; padding: 0; }
.service-link { display: flex; align-items: center; justify-content: space-between; gap: .75rem; min-height: 44px; padding: .75rem 0; text-decoration: none; color: inherit; border-bottom: 1px solid var(--s-line-hairline, #e8e5de); }
.service-link:hover, .service-link:focus-visible { color: var(--rb-teal, #16a49b); }
.service-note { font-size: .85rem; line-height: 1.8; color: var(--s-ink-secondary, #4a6675); margin: .5rem 0 0; }
.service-secondary { background: var(--s-surface-sunken, #f5f3ee); }
.portal-services[dir="ltr"] .service-link .icon { transform: rotate(180deg); }
@media (max-width: 680px) { .services-grid { grid-template-columns: minmax(0, 1fr); } }
</style>
